/**
 * Multiplayer text over /ws/doc (see server/src/docs.rs for the protocol).
 * One connection per open text; Tiptap binds to `doc` and shows cursors from
 * `awareness`.
 */
import * as Y from "yjs";
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate, removeAwarenessStates } from "y-protocols/awareness";

const COLORS = ["#E5484D", "#F76B15", "#E8A03A", "#30A46C", "#12A594", "#0090FF", "#5E6AD2", "#8E4EC6", "#D6409F"];
export function colorFor(id: string): string {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return COLORS[h % COLORS.length];
}

export class DocConnection {
  readonly doc = new Y.Doc();
  readonly awareness: Awareness;
  synced = false;
  private ws: WebSocket | null = null;
  private closed = false;
  private retry = 0;
  private seedPending = false;
  private listeners = new Set<() => void>();
  onSeed: (() => void) | null = null;
  onReset: (() => void) | null = null;

  constructor(
    readonly key: string,
    user: { name: string; color: string },
  ) {
    this.awareness = new Awareness(this.doc);
    this.awareness.setLocalStateField("user", user);
    this.doc.on("update", this.handleDocUpdate);
    this.awareness.on("update", this.handleAwareness);
    window.addEventListener("beforeunload", this.leave);
    this.connect();
  }

  get fragment() {
    return this.doc.getXmlFragment("default");
  }

  subscribe(cb: () => void) {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  /** The editor calls this once it can apply seed content. */
  setSeedHandler(fn: () => void) {
    this.onSeed = fn;
    this.trySeed();
  }

  private trySeed() {
    if (this.seedPending && this.onSeed && this.fragment.length === 0) {
      this.seedPending = false;
      this.onSeed();
    }
  }

  private handleDocUpdate = (update: Uint8Array, origin: unknown) => {
    if (origin !== this) this.send(0, update);
  };

  private handleAwareness = ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }, origin: unknown) => {
    if (origin === this) return;
    this.send(1, encodeAwarenessUpdate(this.awareness, added.concat(updated, removed)));
  };

  private send(kind: number, payload: Uint8Array) {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const buf = new Uint8Array(payload.length + 1);
    buf[0] = kind;
    buf.set(payload, 1);
    this.ws.send(buf);
  }

  private markSynced() {
    if (!this.synced) {
      this.synced = true;
      this.listeners.forEach((l) => l());
    }
  }

  private connect() {
    if (this.closed) return;
    const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws/doc?key=${encodeURIComponent(this.key)}`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      // Send everything we have; edits made while offline reach the server.
      const state = Y.encodeStateAsUpdate(this.doc);
      if (state.byteLength > 2) this.send(0, state);
      this.send(1, encodeAwarenessUpdate(this.awareness, [this.doc.clientID]));
    };
    ws.onmessage = (e) => {
      if (typeof e.data === "string") {
        const msg = JSON.parse(e.data);
        if (msg.type === "init") {
          if (msg.seed) this.seedPending = true;
          if (msg.empty) this.markSynced();
          this.trySeed();
        } else if (msg.type === "reset") {
          this.onReset?.();
        }
        return;
      }
      const data = new Uint8Array(e.data as ArrayBuffer);
      if (data[0] === 0) {
        Y.applyUpdate(this.doc, data.subarray(1), this);
        this.markSynced();
      } else if (data[0] === 1) {
        applyAwarenessUpdate(this.awareness, data.subarray(1), this);
      }
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (!this.closed) setTimeout(() => this.connect(), Math.min(10_000, 500 * 2 ** this.retry++));
    };
  }

  private leave = () => {
    removeAwarenessStates(this.awareness, [this.doc.clientID], "local");
  };

  destroy() {
    this.closed = true;
    this.leave();
    window.removeEventListener("beforeunload", this.leave);
    this.doc.off("update", this.handleDocUpdate);
    this.awareness.off("update", this.handleAwareness);
    this.ws?.close();
    this.ws = null;
    this.awareness.destroy();
    this.doc.destroy();
  }
}
